import { action, get } from "@ember/object"
import Component from "@glimmer/component"
import type {
  CardGoogleClassroom,
  GoogleClassroomClassSetting,
  GoogleClassroomPostType,
} from "@shared/models/card-stack"
import type {
  GoogleClass,
  GoogleClassPostStatus,
  GoogleClassroomCardMediator,
} from "@shared/models/google-classroom"
import type { RotationCalendar, RotationCalendarDate } from "@shared/models/rotation-calendar"
import formatTime from "cc-frontend/helpers/format-time"
import type { GoogleClassroomCardEditor } from "cc-frontend/lib/google-classroom/card-editor"
import { addHours, addMinutes, format, getDay, isBefore, parseISO } from "date-fns"
import { zonedTimeToUtc } from "date-fns-tz"
import { cloneDeep, find, findIndex, isNil, last, nth } from "lodash-es"
import { tracked } from "tracked-built-ins"
import styles from "./class-settings.module.css"

export interface HumanizedRelativeDates {
  sameDay: string
  nextDay: string | null
  endOfWeek: string | null
  endOfNextWeek: string | null
  /** The name of the day at the end of the week, e.g., "Friday". */
  endOfWeekTitle: string | null
}
interface Signature {
  Args: {
    googleClass: GoogleClass
    cardEditor: GoogleClassroomCardEditor
    mediator: GoogleClassroomCardMediator | null
    classSetting: GoogleClassroomClassSetting
    card: CardGoogleClassroom
    dateString: string
    sortedRotationCalendarDates: Array<RotationCalendarDate>
    rotationCalendar: RotationCalendar | null
    timeZoneToPostIn: string
    postType: GoogleClassroomPostType
  }
}
export default class GoogleClassroomClassSettingsComponent extends Component<Signature> {
  readonly styles = styles

  @tracked showMenu = false
  @tracked canShowScheduledDateMenu: boolean = false
  @tracked canShowDueDateMenu: boolean = false

  get minimumDate() {
    if (isNil(this.args.sortedRotationCalendarDates)) return
    return parseISO(this.args.sortedRotationCalendarDates[0].id)
  }

  get maximumDate() {
    if (isNil(this.args.sortedRotationCalendarDates)) return
    let lastDate = last(this.args.sortedRotationCalendarDates)
    if (isNil(lastDate)) return
    return parseISO(lastDate.id)
  }

  get isPostingImmediately() {
    return (
      this.isScheduledBeforeNow &&
      this.args.classSetting.isEnabled &&
      this.postStatus !== "PUBLISHED"
    )
  }

  /**
   * Uses rotation calendar to find due dates relative to scheduledDate using the index of the scheduledDate
   * as a starting point to avoid weekends and days off.
   * Using rotationCal will make it easier to expand our calendar to non-western calendars in the future.
   * and returns HumanizedRelativeDates object which is used to render easily digestible/readable
   * relative dates to the user.
   * We have this same logic on the backend to figure out when to post.
   * So, if this logic changes, make sure to check the backend to update it.
   */
  get humanizedRelativeDates(): HumanizedRelativeDates | undefined {
    if (this.args.sortedRotationCalendarDates === undefined) return
    let sameDayIdx = findIndex(this.args.sortedRotationCalendarDates, (date) => {
      return date.attributes.dateString === this.args.dateString
    })
    let nextDayIdx = sameDayIdx + 1
    let nextDay = nth(this.args.sortedRotationCalendarDates, nextDayIdx)
    let endOfWeekIndex = last(this.args.rotationCalendar?.attributes.schoolDays)
    let endOfWeek = find(this.args.sortedRotationCalendarDates, (date) => {
      let dayIndex = getDay(parseISO(date.attributes.dateString))
      return endOfWeekIndex === dayIndex
    })
    let endOfWeekCounter = 0
    let endOfNextWeek = find(this.args.sortedRotationCalendarDates, (date) => {
      let dayIndex = getDay(parseISO(date.attributes.dateString))
      if (endOfWeekIndex === dayIndex) endOfWeekCounter++
      return endOfWeekCounter === 2
    })

    let formatParse = (string) => {
      return format(parseISO(string), "iii, MMM do")
    }

    return {
      sameDay: formatParse(this.args.dateString),
      nextDay: nextDay?.attributes.dateString ? formatParse(nextDay?.attributes.dateString) : null,
      endOfWeek: endOfWeek?.attributes.dateString
        ? formatParse(endOfWeek?.attributes.dateString)
        : null,
      endOfNextWeek: endOfNextWeek?.attributes.dateString
        ? formatParse(endOfNextWeek?.attributes.dateString)
        : null,
      endOfWeekTitle: endOfWeek?.attributes.dateString
        ? format(parseISO(endOfWeek?.attributes.dateString), "iiii")
        : null,
    }
  }

  get isScheduledBeforeNow() {
    let browserTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (isNil(this.args.classSetting.scheduledAtTime)) return

    // convert current time for user to utc
    let utcTimeForUser = zonedTimeToUtc(new Date(), browserTimeZone)
    // create new Date() using dateString and scheduledAtTime
    let parsedDate = parseISO(this.args.dateString)
    let parsedDateWithTimeForPosting = addMinutes(
      addHours(parsedDate, this.args.classSetting.scheduledAtTime.hour),
      this.args.classSetting.scheduledAtTime.minute
    )

    // convert date object in correct time zone that user will post in to utc
    let utcTimeForPosting = zonedTimeToUtc(parsedDateWithTimeForPosting, this.args.timeZoneToPostIn)
    return isBefore(utcTimeForPosting, utcTimeForUser)
  }

  /** Checks the dueAt.date coming in from the backend and returns an object that gives a
   * renderable relativeDay description and the formatted date that corresponds with the
   * relative day. If the date is a specific due date chosen by the user or null, we omit the
   * relativeDay in the return and only provide the formatted date.
   */
  get humanizedDueDate() {
    if (isNil(this.humanizedRelativeDates)) return
    if (this.args.classSetting?.dueAt === null) {
      return null
    }
    switch (this.args.classSetting?.dueAt?.date) {
      case "SAME_DAY":
        return { relativeDay: "Same Day", formattedDate: this.humanizedRelativeDates.sameDay }
      case "NEXT_DAY":
        return { relativeDay: "Next Day", formattedDate: this.humanizedRelativeDates.nextDay }
      case "END_OF_WEEK":
        return { relativeDay: "End of Week", formattedDate: this.humanizedRelativeDates.endOfWeek }
      case "END_OF_NEXT_WEEK":
        return {
          relativeDay: "End of Next Week",
          formattedDate: this.humanizedRelativeDates.endOfNextWeek,
        }
      default:
        return {
          relativeDay: null,
          formattedDate: format(parseISO(this.args.classSetting?.dueAt?.date), "iii, MMM do"),
        }
    }
  }

  get selectedScheduledTime() {
    if (isNil(this.args.classSetting)) return
    return formatTime(this.args.classSetting?.scheduledAtTime, "12hrs")
  }
  get selectedDueTime() {
    if (isNil(this.args.classSetting)) return
    if (isNil(this.args.classSetting.dueAt)) return null
    if (this.args.classSetting.dueAt.hour === 23 && this.args.classSetting.dueAt.minute === 59) {
      return "End of Day"
    }
    return formatTime(this.args.classSetting?.dueAt, "12hrs")
  }

  /**
   * Checks the isEnabled key on the classSettings object on the lesson card.
   * Only changing this key makes it easy to "save" previously entered scheduled and/or due date/time
   * when a user unschedules a google class.
   *
   * Also aids in rendering the correct scheduling buttons for the user
   * (Ex. an unscheduled class should not give the user an option to set a scheduled time or due date/time)
   */
  get postStatus() {
    if (isNil(this.args.mediator)) return
    let post = find(
      get(get(this.args.mediator, "attributes"), "googleClassStatuses"),
      (classStatus: GoogleClassPostStatus) => {
        return get(classStatus, "googleClassId") === this.args.googleClass.id
      }
    )

    let isEnabled = this.args.classSetting && get(this.args.classSetting, "isEnabled")
    let status = post?.status

    if (status === undefined) {
      // if we don't have a status, we just look at the enabled status in the card
      return isEnabled ? "SCHEDULED" : "UNSCHEDULED"
    } else if (status === "PUBLISHED") {
      // if it's published but we disabled it, the user is trying to delete it
      return isEnabled ? "PUBLISHED" : "DELETED"
    } else if (status === "SCHEDULED") {
      // IF it's scheduled but we disabled it, we mark it as "unscheduled"
      return isEnabled ? "SCHEDULED" : "UNSCHEDULED"
    } else {
      throw Error("Class Setting Status not handled")
    }
  }

  get linkToResource() {
    if (isNil(this.args.mediator)) return
    let post = find(
      get(get(this.args.mediator, "attributes"), "googleClassStatuses"),
      (classStatus: GoogleClassPostStatus) => {
        return get(classStatus, "googleClassId") === this.args.googleClass.id
      }
    )

    return post?.linkToResource
  }

  @action
  scheduleGoogleClass(classId, className) {
    if (isNil(classId)) return
    this.args.cardEditor.scheduleClass(this.args.classSetting, className)
  }

  @action
  unscheduleGoogleClass(classId, className) {
    if (isNil(classId)) return
    this.args.cardEditor.unscheduleClass(this.args.classSetting, className)
  }

  @action
  toggleScheduledDateMenu() {
    if (this.isDestroyed || this.isDestroying) return
    if (this.postStatus === "PUBLISHED") return
    this.canShowScheduledDateMenu = !this.canShowScheduledDateMenu
  }
  @action
  toggleDueDateMenu() {
    if (this.isDestroyed || this.isDestroying) return
    if (this.postStatus === "PUBLISHED") return
    this.canShowDueDateMenu = !this.canShowDueDateMenu
  }

  @action
  closeMenu() {
    if (this.isDestroyed || this.isDestroying) return
    this.canShowScheduledDateMenu = false
  }

  @action setScheduledAtTime({ hour, minute }: { hour: number; minute: number }) {
    let time = cloneDeep(this.args.classSetting.scheduledAtTime)
    time.hour = hour
    time.minute = minute
    this.args.cardEditor.updateClassSetting(this.args.classSetting, "scheduledAtTime", time)
  }
  @action setDueAtTime({ hour, minute }: { hour: number; minute: number }) {
    let dueAt = cloneDeep(
      this.args.classSetting.dueAt || { hour: hour, minute: minute, date: "SAME_DAY" as const }
    )
    dueAt.hour = hour
    dueAt.minute = minute
    this.args.cardEditor.updateClassSetting(this.args.classSetting, "dueAt", dueAt)
  }
}

declare module "@glint/environment-ember-loose/registry" {
  export default interface Registry {
    "Planner::GoogleClassroom::Parts::ClassSettings": typeof GoogleClassroomClassSettingsComponent
  }
}
